export const SCHOOL_LEVELS = ["초등", "중등", "고등", "재수"];
export const CURRENT_YEAR = String(new Date().getFullYear());

export const GRADE_OPTIONS = {
  초등: ["1", "2", "3", "4", "5", "6"],
  중등: ["1", "2", "3"],
  고등: ["1", "2", "3"],
  재수: []
};

export const COURSE_TERM_OPTIONS = [
  { value: "semester_1", label: "1학기" },
  { value: "semester_2", label: "2학기" },
  { value: "summer", label: "여름" },
  { value: "winter", label: "겨울" }
];

export function getInitialGrade(schoolLevel) {
  return GRADE_OPTIONS[schoolLevel]?.[0] || "";
}

export function formatWon(value) {
  return `${Number(value || 0).toLocaleString("ko-KR")}원`;
}

export function getCurrentMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function getPreviousMonthKey(month) {
  if (!/^\d{4}-\d{2}$/.test(month || "")) return "";
  const [year, value] = month.split("-").map(Number);
  const date = new Date(year, value - 2, 1);
  return getCurrentMonthKey(date);
}

export function isEnrollmentActiveInMonth(enrollment, month) {
  const startMonth = enrollment?.startDate?.slice(0, 7) || enrollment?.startMonth;
  const endMonth = enrollment?.endDate?.slice(0, 7) || enrollment?.endMonth;
  if (!startMonth || !month) return false;
  return startMonth <= month && (!endMonth || endMonth >= month);
}

export function isEnrollmentActiveOnDate(enrollment, date) {
  const dateKey = typeof date === "string"
    ? date
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const startDate = enrollment?.startDate || (enrollment?.startMonth ? `${enrollment.startMonth}-01` : "");
  const endDate = enrollment?.endDate || (enrollment?.endMonth
    ? `${enrollment.endMonth}-${String(new Date(Number(enrollment.endMonth.slice(0, 4)), Number(enrollment.endMonth.slice(5, 7)), 0).getDate()).padStart(2, "0")}`
    : "");
  if (!startDate || !dateKey) return false;
  return startDate <= dateKey && (!endDate || endDate >= dateKey);
}

export function getEnrollmentStatus(enrollment, currentDate = new Date()) {
  if (!enrollment) return "종료";
  const currentDateKey = typeof currentDate === "string"
    ? currentDate
    : `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
  const startDate = enrollment.startDate || (enrollment.startMonth ? `${enrollment.startMonth}-01` : "");
  const endDate = enrollment.endDate || enrollment.endMonth;
  if (endDate && endDate < currentDateKey) return "종료";
  if (startDate > currentDateKey) return "예정";
  return "수강 중";
}

export function getTeacherName(teachers, teacherId, fallback = "-") {
  const teacher = teachers.find((item) => item.id === teacherId);
  return teacher?.displayName || teacher?.name || fallback;
}

export function getSubjectName(subjects, subjectId, fallback = "") {
  const subject = subjects.find((item) => item.id === subjectId);
  return subject?.name || fallback || "-";
}

export function getCourseGradeLabel(course) {
  if (!course?.schoolLevel) return "-";
  if (course.schoolLevel === "재수") return "재수";
  return `${course.schoolLevel} ${course.grade || "-"}학년`;
}

export function getCourseGradeOnlyLabel(course) {
  if (!course?.schoolLevel || course.schoolLevel === "재수") return "-";
  return `${course.grade || "-"}학년`;
}

export function getStudentGradeLabel(student) {
  if (!student?.schoolLevel) return student?.grade || "-";
  if (student.schoolLevel === "재수") return "재수";
  return `${student.schoolLevel} ${student.grade || "-"}학년`;
}

export function getStudentPromotion(student) {
  const grade = Number(student?.grade);
  if (!student?.schoolLevel || !Number.isInteger(grade)) return null;

  if (student.schoolLevel === "고등" && grade === 3) {
    return { kind: "graduation", label: "졸업/재원 확인 필요" };
  }

  if (student.schoolLevel === "초등" && grade === 6) {
    return { kind: "promotion", schoolLevel: "중등", grade: "1", schoolConfirmationRequired: true };
  }

  if (student.schoolLevel === "중등" && grade === 3) {
    return { kind: "promotion", schoolLevel: "고등", grade: "1", schoolConfirmationRequired: true };
  }

  const nextGrade = String(grade + 1);
  if (!(GRADE_OPTIONS[student.schoolLevel] || []).includes(nextGrade)) return null;

  return { kind: "promotion", schoolLevel: student.schoolLevel, grade: nextGrade, schoolConfirmationRequired: false };
}

export function normalizeCourseForm(form) {
  const { semester, ...courseForm } = form;
  const days = Array.isArray(form.days) ? form.days : (form.day ? [form.day] : []);
  return {
    ...courseForm,
    year: String(form.year || CURRENT_YEAR),
    term: form.term || form.semester || "semester_1",
    grade: form.schoolLevel === "재수" ? "" : form.grade,
    days,
    day: days[0] || "",
    startTime: form.startTime || "",
    endTime: form.endTime || "",
    capacity: Number(form.capacity || 0),
    tuition: Number(form.tuition || 0)
  };
}

export function courseMatchesStudent(course, student) {
  if (!course || !student) return false;
  if (course.active === false) return false;
  if (student.schoolLevel === "재수") return true;
  if (course.schoolLevel !== student.schoolLevel) return false;
  return course.grade === student.grade;
}

export function getOptionLabel(options, value, fallback = "-") {
  return options.find((option) => option.value === value)?.label || fallback;
}

export function getCourseTermName(course, courseTerms = COURSE_TERM_OPTIONS) {
  return getOptionLabel(courseTerms, course?.term || course?.semester || "semester_1", course?.termLabel || "-");
}

export function getCourseTermLabel(course) {
  const year = course?.year || CURRENT_YEAR;
  return `${year} · ${getCourseTermName(course)}`;
}

export function getCourseLabel(course, subjects, teachers) {
  return [
    getCourseTermLabel(course),
    getCourseGradeLabel(course),
    getSubjectName(subjects, course.subjectId, course.subject || "과목 미지정"),
    course.className || "반 미지정",
    getCourseTimeLabel(course),
    getTeacherName(teachers, course.teacherId, "선생님 미지정"),
    formatWon(course.tuition)
  ].filter(Boolean).join(" · ");
}

export function getCourseCompactLabel(course, subjects, teachers) {
  return [
    getCourseTermLabel(course),
    getSubjectName(subjects, course.subjectId, course.subject || "과목 미지정"),
    course.className || "반 미지정",
    getCourseTimeLabel(course),
    getTeacherName(teachers, course.teacherId, "선생님 미지정")
  ].filter(Boolean).join(" ");
}

export function getCourseTimeLabel(course) {
  if (!course?.startTime && !course?.endTime) return "";
  return `${course.startTime || "시작 미정"}-${course.endTime || "종료 미정"}`;
}

export function getCourseClassNameSummary(courses, courseIds) {
  return [...new Set(courseIds
    .map((courseId) => courses.find((course) => course.id === courseId)?.className)
    .filter(Boolean))]
    .join(", ");
}
